import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  type Anmelderichtlinie,
  type KennwortAblaufWahl,
  anmelderichtlinie as ladeRichtlinie,
  kennwortAblaufSetzen,
} from '../api/verwaltung';
import { ApiFehler } from '../api/fehler';
import { Thema } from '../komponenten/Einstellungen';
import { Meldung } from '../komponenten/Meldung';

/**
 * Anmeldung und Kennwörter (BEN-06 i. d. F. AeA-48, SYS-10).
 *
 * Wunsch vom 05.10.2026 (CRM-58): „in der Administrationsoberfläche eine
 * Einstellungsmöglichkeit zum Ablauf der Passwörter einbauen.“
 *
 * EINE AUSWAHL UND KEIN FREIES FELD. Die übrigen Einstellungen sind freie
 * Werte in sys.einstellung; der Ablauf steht in sys.anmelderichtlinie und hat
 * vier sinnvolle Antworten. Eine freie Zahl lüde zu „7“ ein — und damit zu
 * genau dem Wechselzwang, vor dem der Hinweis daneben warnt.
 *
 * DIE ÜBRIGEN WERTE NUR LESEND. Mindestlänge, Sperre, 2FA-Pflicht und
 * Gerätefrist stehen hier, damit die Entscheidung über den Ablauf im Licht
 * der anderen fällt — vor allem der 2FA-Pflicht. Geändert werden sie wie
 * bisher per Migration.
 *
 * Das Protokoll mit altem und neuem Wert schreibt der Änderungsprotokoll-
 * Trigger an sys.anmelderichtlinie (LOG-02); die Maske muss dafür nichts tun.
 */

const WAHL: readonly { wert: KennwortAblaufWahl; text: string }[] = [
  { wert: null, text: 'nie' },
  { wert: 90, text: 'nach 90 Tagen' },
  { wert: 180, text: 'nach 180 Tagen' },
  { wert: 365, text: 'nach 365 Tagen' },
];

/** `<select>` kennt nur Zeichenketten; „nie“ ist die leere. */
const alsText = (wert: number | null): string => (wert === null ? '' : String(wert));
const alsWahl = (text: string): KennwortAblaufWahl =>
  text === '' ? null : (Number(text) as KennwortAblaufWahl);

export function Kennwortrichtlinie({
  darfKonfigurieren,
}: {
  readonly darfKonfigurieren: boolean;
}): JSX.Element {
  const [richtlinie, setzeRichtlinie] = useState<Anmelderichtlinie | undefined>(undefined);
  const [entwurf, setzeEntwurf] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  const holen = useCallback(async () => {
    try {
      const r = await ladeRichtlinie();
      setzeRichtlinie(r);
      setzeEntwurf(alsText(r.kennwortAblaufTage));
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }, []);

  useEffect(() => {
    void holen();
  }, [holen]);

  async function speichern(): Promise<void> {
    setzeMeldung(null);
    setzeLaeuft(true);
    try {
      const { neu } = await kennwortAblaufSetzen(alsWahl(entwurf));
      await holen();
      setzeMeldung(
        neu === null
          ? 'Kennwörter laufen nicht mehr ab.'
          : `Kennwörter laufen jetzt nach ${neu} Tagen ab.`,
      );
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaeuft(false);
    }
  }

  return (
    <Thema titel="Anmeldung und Kennwörter" breit>
      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      {richtlinie !== undefined && (
        <>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="kennwort-ablauf">
              Kennwörter laufen ab
            </label>
            <select
              id="kennwort-ablauf"
              value={entwurf}
              disabled={!darfKonfigurieren || laeuft}
              aria-describedby="kennwort-ablauf-hilfe"
              onChange={(e) => setzeEntwurf(e.target.value)}
            >
              {WAHL.map((w) => (
                <option key={alsText(w.wert)} value={alsText(w.wert)}>
                  {w.text}
                </option>
              ))}
            </select>
            {/* AeA-48: Hinweis auf BSI und NIST neben der Auswahl. */}
            <div className="feld__hilfe" id="kennwort-ablauf-hilfe">
              <p>
                Das BSI (IT-Grundschutz, ORP.4) und das NIST (SP 800-63B) raten von einem
                regelmäßigen Wechselzwang ab, wenn Kennwörter lang genug sind und ein
                zweiter Faktor gilt: Er führt zu vorhersehbaren Kennwörtern wie
                „Sommer2026“ → „Herbst2026“. Ein Ablauf ist vor allem dort sinnvoll, wo
                kein zweiter Faktor verlangt wird.
              </p>
              <p>
                Beim Einschalten läuft kein Kennwort sofort ab — jedes hat die volle Frist ab
                diesem Tag. Wer die Frist bei laufendem Ablauf verkürzt, verkürzt sie auch für
                die laufenden Kennwörter. Ein abgelaufenes Kennwort sperrt nicht: Bei der
                nächsten Anmeldung wird ein neues verlangt, 14 Tage vorher warnt „Mein
                Konto“.
              </p>
            </div>
          </div>

          {darfKonfigurieren && (
            <div className="knopfreihe">
              <button
                type="button"
                className="knopf knopf--klein"
                disabled={laeuft || entwurf === alsText(richtlinie.kennwortAblaufTage)}
                onClick={() => void speichern()}
              >
                Ablauf speichern
              </button>
            </div>
          )}

          <dl className="angaben" aria-label="Weitere Werte der Anmelderichtlinie">
            <dt>Mindestlänge</dt>
            <dd>{richtlinie.mindestlaenge} Zeichen</dd>
            <dt>Sperre</dt>
            <dd>
              nach {richtlinie.fehlversucheBisSperre} Fehlversuchen für{' '}
              {richtlinie.sperrdauerMinuten} Minuten
            </dd>
            <dt>Zweiter Faktor</dt>
            <dd>{richtlinie.zweiFaktorPflicht ? 'Pflicht für alle Konten' : 'freiwillig'}</dd>
            <dt>Gemerkte Geräte</dt>
            <dd>
              {richtlinie.geraetGueltigTage === 1
                ? 'gültig bis zur nächsten Mitternacht'
                : `gültig bis zur ${richtlinie.geraetGueltigTage}. Mitternacht`}
            </dd>
          </dl>
          <p className="feld__hilfe">
            Diese Werte sind hier nur zu sehen; sie werden mit einer Programmänderung
            angepasst.
          </p>
        </>
      )}
    </Thema>
  );
}
